<script>
import moment from "moment";
import { mapGetters } from "vuex";
import workflowService from "@/api/ativacaoped/workflow.service";
import Timeline from "./horizontal-timeline.vue";
import VerticalTimeline from "./vertical-timeline.vue";
import { filterSysComOut } from "../../config/comments.config";
import { slaLabelCorStatus, slaLabelCorTexto } from "../../config/sla-label";
import { etapaComFinal, reordenarEtapasParaFluxo } from "../../configuracoes/windows/workflow/utils/workflow-order-utils";

/**
 * @typedef {Object} ParsedStep
 * @property {string} id - ID da etapa
 * @property {string} nome - Nome da etapa
 * @property {boolean} completed - Se a etapa está completa
 * @property {boolean} current - Se é a etapa atual
 * @property {boolean} [repproved] - Se foi reprovada
 * @property {Array<Object>} comments - Comentários da etapa
 */

export default {
  components: {
    Modal: () => import("@/components/g-modal.vue"),
    Timeline,
    VerticalTimeline
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      commentsModal: {
        opened: false
      },
      /** limite de etapas visiveis no card */
      maxStepsLength: 7,
      /** Etapas reais carregadas do backend (uma vez, com cache) */
      workflowEtapas: [],
      workflowEtapasLoaded: false
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    /**
     * Lista de comentários dos usuários
     * @type {import('../../config/comments.config').Comment[]}
     * @readonly
     */
    comments() {
      return this.value?.comentarios ?? [];
    },
    /**
     * Etapas na ordem do fluxo parametrizado: o caminho previsto do BP
     * (início → Aprovação final) vem primeiro, derivado dos encaminhamentos
     * (mesma previsão usada na grade de Configurações); as etapas fora do
     * caminho mantêm a ordem do array do backend. O campo `ordem` é
     * descontinuado e NÃO é usado.
     * @type {Array}
     * @readonly
     */
    orderedEtapas() {
      const { etapasOrdenadas } = reordenarEtapasParaFluxo(
        this.workflowEtapas || []
      );
      return etapasOrdenadas;
    },
    /**
     * Etapa terminal de aprovação — etapa que possui um status com
     * encaminhamento `final: "aprovado"` (independente do nome).
     * @type {Object|undefined}
     * @readonly
     */
    approvalEtapa() {
      return etapaComFinal(this.orderedEtapas, "final_aprovado");
    },
    /**
     * Etapa terminal de reprovação — etapa que possui um status com
     * encaminhamento `final: "reprovado"` (independente do nome).
     * @type {Object|undefined}
     * @readonly
     */
    reprovalEtapa() {
      return etapaComFinal(this.orderedEtapas, "final_reprovado");
    },
    /**
     * @type {Array<ParsedStep>} firstParameters - Primeiras etapas (ocultas)
     * @readonly
     */
    firstParameters() {
      if (this.parameters.length < this.maxStepsLength) {
        return [];
      }

      return this.parameters.slice(0, -this.maxStepsLength);
    },
    /**
     * @type {Array<ParsedStep>} lastParameters - Últimas etapas (visíveis)
     * @readonly
     */
    lastParameters() {
      if (this.parameters.length <= this.maxStepsLength) {
        return this.parameters;
      }

      return this.parameters.slice(-this.maxStepsLength);
    },
    /**
     * @type {Array<ParsedStep>} parameters - Todas as etapas processadas
     * @readonly
     */
    parameters() {
      const comments = [...this.comments].sort((a, b) =>
        a.data?.localeCompare(b.data || "")
      );
      const etapas = this.orderedEtapas;

      if (!etapas.length) {
        return [];
      }

      const aprovalEtapa = this.approvalEtapa;
      const reprovalEtapa = this.reprovalEtapa;
      const aprovalId = aprovalEtapa?.id;
      const reprovalId = reprovalEtapa?.id;

      const atual = etapas.find(
        ({ id }) => id === this.value?.workflow?.id
      ) || (etapas.length ? etapas[0] : undefined);

      if (!comments.length) {
        return etapas
          .filter(({ id }) => !this.isEtapaAjustePorId(id))
          .map((etapa, i) => ({
            ...etapa,
            completed: !!aprovalId && etapa.id === aprovalId,
            current: !i
          }));
      }

      const completedSteps = comments.map(comment => {
        const etapa = etapas.find(
          ({ id }) => id === comment.workflow_id
        ) || {};

        return {
          ...etapa,
          completed: true,
          current: false,
          comments: [comment]
        };
      });

      if (
        completedSteps.length > 0 &&
        completedSteps.at(-1).id === atual?.id
      ) {
        completedSteps.pop();
      }

      const isReproved = !!reprovalId && atual?.id === reprovalId;

      const currentStep = atual
        ? {
            ...atual,
            completed: !!aprovalId && atual.id === aprovalId,
            current: true,
            repproved: isReproved,
            comments: []
          }
        : null;

      const nextSteps = [];
      let current = atual;

      if (current && !isReproved) {
        const currentIndex = etapas.findIndex(({ id }) => id === current.id);
        nextSteps.push(...etapas.slice(currentIndex + 1));
      }

      const incompletedSteps = nextSteps.map(etapa => ({
        ...etapa,
        completed: false,
        current: false,
        comments: []
      }));

      const concatAndParseComments = (...items) => {
        let lastId = null;

        return items.reduceRight((acc, item) => {
          if (!item) {
            return acc;
          }

          if (lastId !== item.id) {
            lastId = item.id;

            return [item, ...acc];
          }

          acc[0].comments.unshift(...item.comments);

          return acc;
        }, []);
      };

      if (isReproved) {
        const reprovada = reprovalEtapa
          ? {
              ...reprovalEtapa,
              completed: true,
              current: true,
              repproved: true,
              comments: []
            }
          : null;

        incompletedSteps.splice(-1, 1, reprovada);

        return concatAndParseComments(...completedSteps, ...incompletedSteps);
      }

      return concatAndParseComments(
        ...completedSteps,
        currentStep,
        ...incompletedSteps
      );
    },
    parsedDate() {
      const dateA = moment(this.value.data_inicio, "YYYY-MM-DD");
      const dateB = moment(this.value.data_fim, "YYYY-MM-DD");
      const parsed = [dateA, dateB].map(date =>
        date.isValid() ? date.format("DD/MM/YYYY") : "Data indefinida."
      );
      return parsed.join(" — ");
    },
    slaColor() {
      return slaLabelCorTexto(this.value.sla_label);
    },
    statusColor() {
      return slaLabelCorStatus(this.value.sla_label);
    },
    userCommentsLength() {
      return this.comments.filter(filterSysComOut).length;
    }
  },
  methods: {
    /**
     * Determina se a etapa possui um status configurado como ajuste (tipo
     * parametrizado: slug/nome contendo "ajuste"). Não depende de id fixo
     * (`status_id === 3` foi descontinuado junto com o campo `ordem`).
     * @param {number} id
     * @returns {boolean}
     */
    isEtapaAjustePorId(id) {
      const etapa = this.orderedEtapas.find(({ id: etapaId }) => etapaId === id);

      if (!etapa || !Array.isArray(etapa.status)) {
        return false;
      }

      return etapa.status.some(status => {
        const slug = status?.tipo?.slug;
        const nome = status?.tipo?.nome;

        return (
          (slug && String(slug).toLowerCase().includes("ajuste")) ||
          (nome && String(nome).toLowerCase().includes("ajuste"))
        );
      });
    },
    goToBP() {
      this.$router.push({
        name: "ativacaoped-projetos-form-edicao",
        params: { id: this.value.id }
      });
    },
    showDots() {
      return this.firstParameters.length > 0;
    },
    async loadWorkflowEtapas() {
      if (this.workflowEtapasLoaded) {
        return;
      }

      try {
        const etapas = await workflowService.getWorkflows(this.clientId);
        this.workflowEtapas = Array.isArray(etapas) ? etapas : [];
      } catch (error) {
        this.notify(error);
        this.workflowEtapas = [];
      } finally {
        this.workflowEtapasLoaded = true;
      }
    }
  },
  async created() {
    await this.loadWorkflowEtapas();
  }
};
</script>

<template>
  <v-card class="px-2" elevation="4">
    <div class="content-container">
      <div class="group">
        <div class="item">
          <h1>{{ value.nome }}</h1>
          <span>{{ parsedDate }}</span>
        </div>
        <div class="item">
          <h4>SLA</h4>
          <span :style="{ color: slaColor }">{{ value.sla_label }}</span>
        </div>
      </div>
      <div class="group">
        <div class="item">
          <Timeline
            :hiddenSteps="firstParameters.length"
            :visibleSteps="lastParameters"
          />
        </div>
      </div>
    </div>

    <div class="action-container">
      <div class="status" :style="{ color: statusColor }">
        <v-icon large>mdi-circle-small</v-icon>
        <span>{{ value.workflow.nome }}</span>
      </div>
      <div class="buttons">
        <v-badge
          color="secondary"
          :content="userCommentsLength"
          offset-y="16"
          offset-x="24"
          :value="userCommentsLength"
        >
          <v-btn color="secondary" text @click="commentsModal.opened = true">
            Ver todas etapas e comentários
          </v-btn>
        </v-badge>

        <v-btn color="secondary" depressed @click="goToBP">
          Visualizar Business Plan
        </v-btn>
      </div>
    </div>

    <Modal
      v-model="commentsModal.opened"
      hide-buttons
      title="Comentários"
      width="max-content"
    >
      <VerticalTimeline :steps="parameters" />
    </Modal>
  </v-card>
</template>

<style lang="scss" scoped>
@import "@/scss/_vuetify.scss";

.action-container,
.content-container {
  display: flex;
  flex-direction: row;
  padding: 1rem;
  width: 100%;
}

.action-container {
  align-items: center;
  justify-content: space-between;

  .status {
    span {
      color: inherit;
      font-size: 0.75rem;
      font-weight: 300;
    }

    .v-icon {
      color: inherit;
      font-size: 0.75rem;
    }
  }

  .v-btn {
    padding: 0.625rem 1rem;
    margin: 0 0.25rem;
  }
}

.content-container {
  border-bottom: thin solid #00000040;
  display: flex;
  flex-direction: row;

  @include md {
    flex-direction: column;
  }

  h1,
  h4 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  h4 {
    font-size: 0.75rem;
  }

  span {
    color: #c5c7d0;
    font-size: 0.75rem;
  }

  .group {
    display: flex;
    flex-direction: column;
    width: 40%;
  }

  .group + .group {
    width: 60%;
  }

  .item {
    height: 100%;
  }

  .item + .item {
    margin-top: 0.5rem;
  }
}
</style>
